import { useChat } from '@ai-sdk/react'
import { DefaultChatTransport, isTextUIPart, isToolUIPart } from 'ai'
import type { DynamicToolUIPart, ToolUIPart, UIMessage } from 'ai'
import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { REMARK_PLUGINS } from './markdown'
import { Icon, PATH, StopIcon } from '../icons'
import { SkipQuestionError, fetchConversationUserView, skipPendingQuestion } from '../lib/api'
import { createToolElapsedStore, readToolElapsedData } from '@llj/ai-agent/dist/ai-chat/chat/tool-elapsed'
import { isTurnNoOutputData } from '@llj/ai-agent/dist/ai-chat/chat/turn-no-output'
import { toChatMessages } from './history-messages'
import { ToolAttachments } from './attachments/ToolAttachments'
import { DocumentPreview } from './DocumentPreview'
import { FeedbackMenu } from './FeedbackMenu'
import { WHOLE_CONVERSATION_TURN, feedbackAnchors, feedbackForTurn } from './feedback-entries'
import { useConversationFeedback } from './use-conversation-feedback'
import { toolGroupAnchorId } from './scroll-follow'
import { SuggestionBar } from './SuggestionBar'
import { fetchQuickQas, type QuickQaSuggestion } from './suggestions'
import {
  createDurationTracker,
  describeTool,
  formatDuration,
  groupConsecutiveTools,
  groupDurationText,
  hasVisibleText,
  isElapsedApplicable,
  isStepBoundary,
  purposeText,
  resolveElapsed,
  summarizeToolGroup,
  totalElapsed,
} from './tool-group'
import { useScrollFollow } from './use-scroll-follow'
import {
  parseChoiceValue, storeDebug, storeModel, toChoiceValue, useModelChoice,
} from './model-choice'
import {
  clampSteps, storeExperiment, thinkingBudgetOptions, toExperimentRequest, useExperimentChoice,
} from './experiment-choice'
import type { ClientModelOption, StepBounds } from '../client-config'
import { QuestionPanel } from './QuestionPanel'
import { QuestionRecord } from './QuestionRecord'
import { ConfirmCard } from './ConfirmCard'
import {
  abandonedOutput,
  confirmCardStatus,
  confirmRequestBody,
  findPendingConfirmPart,
  isConfirmCardPart,
  isConfirmRequest,
  toolNameOf,
} from './confirm'
import { abandonedResult } from '@llj/ai-agent/dist/tools/pause/confirm/result'
import {
  ALREADY_ANSWERED_CODE,
  PENDING_QUESTION_CODE,
  type PendingAnswer,
  type QuestionDraft,
  type SubmittedAnswer,
  answerRequestBody,
  findPendingQuestionPart,
  isAnswerRequest,
  isQuestionSetPart,
  markQuestionUnanswered,
  readAskInput,
  shouldSendAnswer,
  toAnsweredOutput,
} from './ask-user'

type AnyToolPart = ToolUIPart | DynamicToolUIPart

// 耗時只認 toolCallId，所以整頁共用一份就夠；換對話時 id 也換，不會串味。
const durations = createDurationTracker()

// 伺服器在串流裡另外送的真值（#1406）。SDK 把掛在 tool-output 上的 toolMetadata
// 丟掉了，所以即時那一份改從這裡讀；它與事件流記的是同一個整數。
const serverElapsed = createToolElapsedStore()

// part 的欄位隨 state 分岔，先攤平成 tool-group 要的純資料再交給它判斷。
const readToolPart = (part: AnyToolPart) => describeTool({
  state: part.state,
  input: part.input,
  output: part.state === 'output-available' ? part.output : undefined,
  errorText: part.state === 'output-error' ? part.errorText : undefined,
  metaFailed: part.toolMetadata?.failed,
  metaOutcome: part.toolMetadata?.outcome,
})

// 每一支各自的耗時（毫秒）。伺服器記下的值優先：即時走 data part、歷史走
// toolMetadata，兩者同一個整數。還在跑的那一支伺服器還沒給，退回前端計時器（#1399）。
// 🔴 被拒絕的那一支一律不給秒數：拒絕不是一次執行，計時器量到的是使用者猶豫的時間。
const readElapsedMs = (part: AnyToolPart): number | null => {
  if (!isElapsedApplicable(part.state)) return null
  return resolveElapsed(
    serverElapsed.read(part.toolCallId) ?? part.toolMetadata?.elapsedMs,
    durations.observe(part.toolCallId, part.state, Date.now()),
  )
}

const readDuration = (part: AnyToolPart): string | null => {
  const elapsed = readElapsedMs(part)
  return elapsed === null ? null : formatDuration(elapsed)
}

// 附件卡片畫在摘要行下方而不是夾在工具之間（#1620），工具列因此一律可併進摘要行。
// 🔴 提問不併進「查了幾項」的摺疊列（#1640）：開場白與答案摘要要直接出現在對話區。
// 確認卡同樣不併（#1693）：要按確認的東西不能藏在摺疊列裡
const isGroupableTool = (part: UIMessage['parts'][number]): boolean =>
  isToolUIPart(part) && !isQuestionSetPart(part) && !isConfirmCardPart(part)

// 展開／收合要往上報，捲動跟隨才停得下來（#1334）。
type ToggleReport = (id: string, open: boolean) => void

// 🔴 用 summary 的 click 而非 details 的 toggle：toggle 是非同步派發的，
// 那時自動捲到底已經先跑過一輪，量到的錨點位置是被拉走之後的，錨了也沒用。
const reportSummaryClick = (id: string, report: ToggleReport) =>
  (e: React.MouseEvent<HTMLElement>) => {
    const details = e.currentTarget.closest('details')
    if (details) report(id, !details.open)
  }

// toggle 補一道保險（鍵盤或程式改 open）。同一則重複回報是冪等的。
const reportToggle = (id: string, report: ToggleReport) =>
  (e: React.SyntheticEvent<HTMLDetailsElement>) => {
    if (e.target !== e.currentTarget) return
    report(id, e.currentTarget.open)
  }

interface ToolCardProps {
  part: AnyToolPart
  onToggle: ToggleReport
  // 原始送出與收到的 JSON 只給 dev 看（#1724）
  showDetails: boolean
}

function ToolCard({ part, onToggle, showDetails }: ToolCardProps) {
  const { purpose, note, running, failed, outcome } = readToolPart(part)
  const elapsed = readDuration(part)
  // 🔴 主文是這一支自己的意圖，不是工具名（#1384）；缺值退回「資料查詢」。
  // 🔴 四態各有自己的樣式鉤子：拒絕與待補充不是失敗，不套紅字（#1414）。
  const className =
    `toolcard ${running ? 'running' : ''} ${failed ? 'failed' : ''} outcome-${outcome}`
  const line = (
    <>
      <span className="toolcard-mark" />
      <span className="toolcard-purpose">{purposeText(purpose)}</span>
      <span className="toolcard-note">{note}</span>
      {elapsed && <span className="toolcard-time">{elapsed}</span>}
    </>
  )
  // 非 dev 沒有細節可展開，只畫那一行：留著 details 會變成點了沒反應的空殼
  if (!showDetails) {
    return (
      <div className={className} data-anchor-id={part.toolCallId}>
        <div className="toolcard-line">{line}</div>
      </div>
    )
  }
  return (
    <details
      className={className}
      data-anchor-id={part.toolCallId}
      onToggle={reportToggle(part.toolCallId, onToggle)}
    >
      <summary onClick={reportSummaryClick(part.toolCallId, onToggle)}>{line}</summary>
      <pre>{JSON.stringify({ 送出: part.input, 收到: part.output ?? part.errorText }, null, 2)}</pre>
    </details>
  )
}

// 連續的工具呼叫收成一行摘要（#1315）。收起來的是細節，不是狀態：
// 失敗與執行中直接標在摘要行上，不必展開就知道出事了。
function ToolGroup({ parts, onToggle, showDetails }: Omit<ToolCardProps, 'part'> & {
  parts: AnyToolPart[]
}) {
  const statuses = parts.map(readToolPart)
  const summary = summarizeToolGroup(statuses)
  // 整組工具的執行耗時加總（#1399）：跑完之後還在，重新整理後仍是同一個數字。
  // 進行中就已經在加總，完成那一刻不會突然冒出一個之前沒有的欄位。
  const total = totalElapsed(parts.map(readElapsedMs))
  const elapsed = total === null ? null : groupDurationText(total)
  const flavour = [
    summary.running ? 'running' : '', summary.failed ? 'failed' : '',
    summary.attention ? 'attention' : '',
  ].filter(Boolean).join(' ')
  // 🔴 不能沿用第一張卡的 toolCallId（#1404）：撞號會讓收合第一張卡被誤判成整組收好
  const groupId = toolGroupAnchorId(parts[0].toolCallId)
  return (
    <details
      className={`toolgroup ${flavour}`}
      data-anchor-id={groupId}
      onToggle={reportToggle(groupId, onToggle)}
    >
      <summary onClick={reportSummaryClick(groupId, onToggle)}>
        <span className="toolgroup-chevron" aria-hidden="true" />
        <span className="toolgroup-text">{summary.text}</span>
        {elapsed && <span className="toolgroup-time">{elapsed}</span>}
        {summary.flag && <span className="toolgroup-flag">{summary.flag}</span>}
      </summary>
      <div className="toolgroup-body">
        {parts.map((part) => (
          <ToolCard
            key={part.toolCallId} part={part} onToggle={onToggle} showDetails={showDetails}
          />
        ))}
      </div>
    </details>
  )
}

// 模型思考（#1739）：僅 dev 渲染，外觀比照工具卡、預設折疊。
// 非 dev 伺服器本來就不送思考，這裡再擋一次是為了「不渲染」不靠伺服器單方面保證
function ReasoningBlock({ text }: { text: string }) {
  // 空字串直接不畫：串流剛開始的空 part 會留一個展不開的空殼
  if (!text.trim()) return null
  return (
    <details className="toolcard reasoning" data-testid="reasoning-block">
      <summary>
        <span className="toolcard-mark" />
        <span className="toolcard-purpose">思考過程</span>
      </summary>
      <div className="reasoning-body">{text}</div>
    </details>
  )
}

// 409 的預設說法。伺服器通常會給一句更精確的，取不到時用這句。
const CONFLICT_NOTICE = '這條對話已在別處更新，已重新載入。'

// 有待回答的提問卻送出了新的一句（#1640）。🔴 不叫人重新載入：畫出提問面板，
// 使用者可以回答，或直接再送一次這句話來略過它。
const PENDING_QUESTION_NOTICE = '上方還有等你回答的問題；直接再送出一次會略過它。'

// 有提問面板時輸入框的提示字（#1653）：直接回覆＝整組不回答
const SKIP_PLACEHOLDER = '或直接回覆…'

// 送出答案被伺服器擋下時的預設說法（#1642）。伺服器通常會給更精確的一句
const ANSWER_FAILED_NOTICE = '答案沒有送出，已重新載入提問，請再試一次。'

// 確認被伺服器擋下時的預設說法（#1693）。伺服器通常會給更精確的一句
const CONFIRM_FAILED_NOTICE = '確認沒有送出，已重新載入對話，請再試一次。'

// 取消或中斷確認卡失敗時的說法（#1693）。已重讀對話，卡片回到伺服器的真實狀態
const CONFIRM_ABANDON_FAILED = '取消沒有成功，已重新載入對話，請再試一次。'

// 按 ✕ 關閉失敗時的說法（#1653）。api 的預設說法講的是「這句話還沒送出」，這裡不適用
const DISMISS_NOTICES = {
  failed: '關閉提問失敗，請再試一次。',
  alreadyAnswered: '這組提問已經回答過了，已重新載入對話。',
}

// 按下停止時告訴伺服器「這條對話別跑了」。失敗就吞掉：本地那一端照樣停得了，
// 而停止鍵本身不該因為這一支失敗就在畫面上留一句紅字（#1413）。
const requestServerStop = async (conversationId: string | null): Promise<void> => {
  if (!conversationId) return
  await fetch('/api/ai/chat/stop', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ conversationId }),
  }).catch(() => undefined)
}

interface ChatPageProps {
  // null ＝ 開一條新的；有值 ＝ 續談那一條
  conversationId: string | null
  // 這條對話的 id 一確定就往上報，側欄才畫得出 active
  onConversationReady: (conversationId: string) => void
  // 一輪結束（標題此時才有值）通知側欄重拉清單
  onTurnFinished: () => void
  // 伺服器判定為 dev 才有模型選單與其 -debug 選項；偵錯元素再看是否選了 -debug（#1724）
  showDevTools: boolean
  // dev 的可選模型與預設（#1726）。非 dev 時伺服器不給，模型切換與標示就不畫
  modelOptions?: ClientModelOption[]
  defaultModel?: string | null
  // dev 實驗參數的步數上下界（#1727）。沒給就不畫實驗參數、請求也不帶
  stepBounds?: StepBounds | null
  // 思考預算上下界（#1739）；沒有就不顯示該設定
  thinkingBounds?: StepBounds | null
  // 連結帶入的預填訊息（只有新對話會給），掛載時填進輸入框
  prefill?: string | null
}

const NO_MODEL_OPTIONS: ClientModelOption[] = []

export function ChatPage(props: ChatPageProps) {
  const { conversationId: resumeId, onConversationReady, onTurnFinished, showDevTools } = props
  const modelOptions = showDevTools ? props.modelOptions ?? NO_MODEL_OPTIONS : NO_MODEL_OPTIONS
  // 續談時伺服器說這條對話最新一則用了哪個模型（#1726）；新對話是 null
  const [conversationModel, setConversationModel] = useState<string | null>(null)
  // 選定的模型跟著每一種請求送出（訊息、回答、確認）；transport 只建一次，所以走 ref
  const { model: selectedModel, debug, choose: chooseModel } = useModelChoice(
    modelOptions, props.defaultModel ?? null, conversationModel,
  )
  // 偵錯模式＝dev 且選了 -debug 選項。工具 JSON、思考、回饋、實驗參數都只看它
  const debugMode = showDevTools && debug
  const selectedModelRef = useRef({ model: selectedModel, debug: debugMode })
  selectedModelRef.current = { model: selectedModel, debug: debugMode }
  // dev 實驗參數（#1727）：非偵錯模式不畫也不送；切回時由 hook 重讀本機記住的值
  const stepBounds = debugMode ? props.stepBounds ?? null : null
  const thinkingBounds = debugMode ? props.thinkingBounds ?? null : null
  const { state: experiment, update: updateExperiment } =
    useExperimentChoice(stepBounds, thinkingBounds)
  const experimentRef = useRef({ experiment, stepBounds, thinkingBounds })
  experimentRef.current = { experiment, stepBounds, thinkingBounds }
  const [input, setInput] = useState(() => props.prefill ?? '')
  const [ready, setReady] = useState(false)
  const [setupError, setSetupError] = useState<string | null>(null)
  // transport 只建一次，所以 conversationId 走 ref 而非 state。
  const conversationIdRef = useRef<string | null>(null)
  // 留意見要靠 state 才觸發重繪；ref 是給 transport 讀的，兩者同一個值（#1446）
  const [feedbackConversationId, setFeedbackConversationId] = useState<string | null>(resumeId)
  // 送出時要帶的樂觀鎖基準：這個瀏覽器看到的最後一個事件序號（#1330）。
  // 新對話是 0；每一輪結束後重讀，因為那一輪寫了幾筆事件只有伺服器知道。
  const lastSequenceRef = useRef(0)
  // 最近一次停止請求；它回應時伺服器那一輪已收尾，重讀基準要等它
  const stopRequestRef = useRef<Promise<void>>(Promise.resolve())
  // 收到 409 時顯示的提示。🔴 不自動重送：那會把使用者的話接在別人的對話後面。
  const [staleNotice, setStaleNotice] = useState<string | null>(null)
  // 略過提問的進行中與失敗（#1640）。失敗時那句話留在輸入框、不送出
  const [skipping, setSkipping] = useState(false)
  const [skipError, setSkipError] = useState<string | null>(null)
  // 最後送出的那句話。伺服器說「有待回答的提問」時放回輸入框，字不會不見
  const lastSentTextRef = useRef('')
  // 剛填進提問、等著送出的答案（#1642）。續跑請求取用後清掉，串完就不會再觸發
  const pendingAnswerRef = useRef<PendingAnswer | null>(null)
  // 面板的作答草稿，依 toolCallId 記。送出失敗重讀後面板重掛，答過的不必再填
  const draftsRef = useRef(new Map<string, QuestionDraft[]>())
  // 剛按下確認、等著送出的那一張（#1693）。續跑請求取用後清掉，只送一次
  const pendingConfirmRef = useRef<string | null>(null)
  // 按下確認到伺服器回結果之間，卡片顯示處理中、按鈕停用
  const [confirmingId, setConfirmingId] = useState<string | null>(null)

  // 建議提問由後台維護（#1365）。端點帶一小時的 Cache-Control，
  // 所以掛載時直接取即可，不必自己記住上次拿到什麼。
  const [quickQas, setQuickQas] = useState<QuickQaSuggestion[]>([])
  useEffect(() => {
    void fetchQuickQas().then(setQuickQas)
  }, [])

  // 側邊預覽正在看哪一份公文（#1620）；null ＝ 沒開
  const [previewDocumentId, setPreviewDocumentId] = useState<string | null>(null)
  const closePreview = useCallback(() => setPreviewDocumentId(null), [])

  // 被使用者按停止的那幾則。停止靠 useChat 的 stop()，這裡只記「標在誰後面」。
  const [abortedIds, setAbortedIds] = useState<string[]>([])

  // 模型這一輪什麼都沒回的那幾則（#1473）。🔴 措辭不能說「失敗」：
  // 它沒出錯，只是沒講話，兩者會讓里長做出不同的反應。
  const [noOutputIds, setNoOutputIds] = useState<string[]>([])
  // 即時那一輪的同一件事。串流結束才知道，那時還配不到訊息代號，
  // 所以只記「有沒有發生」，畫的時候掛在最後一則沒有內容的助理訊息上。
  const [liveNoOutput, setLiveNoOutput] = useState(false)

  const {
    messages, setMessages, sendMessage, addToolOutput, stop, status, error,
  } = useChat({
    // 只在提問剛拿到答案時自動送出續跑（#1642），其餘工具都在伺服器執行
    sendAutomaticallyWhen: ({ messages: current }) =>
      shouldSendAnswer(current, pendingAnswerRef.current),
    // 伺服器量到的工具耗時（#1406）。transient data part 不進 message.parts，
    // 只能在這裡收；它排在 tool-output 那一筆之前，重繪時值已經就位。
    onData: (chunk) => {
      const elapsed = readToolElapsedData(chunk)
      if (elapsed) serverElapsed.record(elapsed)
      if (isTurnNoOutputData(chunk)) setLiveNoOutput(true)
    },
    transport: new DefaultChatTransport({
      api: '/api/ai/chat',
      // AI SDK 預設把整份歷史送上來；契約 §7.6 要的是伺服器重組，所以只送這一句。
      prepareSendMessagesRequest: ({ messages: sent }) => {
        // 有選模型、有調實驗參數才帶（#1726、#1727）：回答與確認續跑那一輪也一樣。
        // 🔴 送出這一刻才記到本機：切了沒送出不算，下一條新對話不會沿用它
        const withModel = <T extends object>(body: T) => {
          const { model, debug: debugChosen } = selectedModelRef.current
          if (model) {
            storeModel(model)
            storeDebug(debugChosen)
          }
          const { experiment: current, stepBounds: bounds, thinkingBounds: thinking } =
            experimentRef.current
          // 🔴 剛切回一般選項、effect 還沒清掉 state 的那一刻，bounds 已是 null，殘值不寫
          if (current && bounds) storeExperiment(current)
          const request = current && bounds ? toExperimentRequest(current, bounds, thinking) : null
          return {
            body: { ...body, ...(model ? { model } : {}), ...(request ? { experiment: request } : {}) },
          }
        }
        // 🔴 回答續跑只送 toolCallId 與答案，不送整則訊息；取用即清，只送一次（#1642）
        const answer = pendingAnswerRef.current
        if (answer) {
          pendingAnswerRef.current = null
          return withModel(answerRequestBody(conversationIdRef.current, answer))
        }
        // 🔴 確認續跑同樣只送 toolCallId 與決定，參數以伺服器事件流為準（#1693）
        const confirmId = pendingConfirmRef.current
        if (confirmId) {
          pendingConfirmRef.current = null
          return withModel(confirmRequestBody(conversationIdRef.current, confirmId))
        }
        return withModel({
          conversationId: conversationIdRef.current,
          message: sent[sent.length - 1],
          baseSequence: lastSequenceRef.current,
        })
      },
      // 409 ＝ 這條對話已在別處更新。提示並重新載入，不靜默吞掉也不自動重送。
      fetch: (async (url: RequestInfo | URL, init?: RequestInit) => {
        const res = await fetch(url, init)
        // 送出答案或確認被擋下（#1642、#1693）：重讀才回到伺服器的真實狀態。
        // isAnswerRequest 認的是帶 toolCallId 的續跑，確認請求同樣命中
        if (!res.ok && isAnswerRequest(init)) {
          const body = await res.clone().json().catch(() => null) as { message?: string } | null
          void reloadConversation()
          const fallback = isConfirmRequest(init) ? CONFIRM_FAILED_NOTICE : ANSWER_FAILED_NOTICE
          throw new Error(body?.message ?? fallback)
        }
        if (res.status !== 409) return res
        const body = await res.clone().json().catch(() => null) as
          { message?: string; code?: string } | null
        // 🔴 有待回答的提問（#1640）不是「別處更新」：重讀使用者視圖把面板畫出來，
        // 那句話放回輸入框，再送一次就會先略過那組提問
        if (body?.code === PENDING_QUESTION_CODE) {
          setInput(lastSentTextRef.current)
          void reloadConversation()
          throw new Error(PENDING_QUESTION_NOTICE)
        }
        setStaleNotice(body?.message ?? CONFLICT_NOTICE)
        void reloadConversation()
        throw new Error(CONFLICT_NOTICE)
      }) as typeof fetch,
    }),
  })

  // 🔴 切換對話時把前一條的串流停掉。ChatPage 由 conversationId 當 key，
  // 換對話＝卸載重掛；不停的話那一條的回應會靜默寫進新開的畫面。
  const stopRef = useRef(stop)
  stopRef.current = stop
  useEffect(() => () => {
    void stopRef.current()
  }, [])

  // 對話載入：訊息、中止標記、樂觀鎖基準都在這裡一起落地。
  // 收到 409 時也走這條——重新載入本來就是使用者該看到的下一個狀態（#1330）。
  async function reloadConversation() {
    const id = conversationIdRef.current
    if (!id) return
    const view = await fetchConversationUserView(id)
    setMessages(toChatMessages(view))
    // 被中止那幾則的標記一起帶回來，續談時看得到它當初停在哪
    setAbortedIds(view.messages.filter((m) => m.aborted).map((m) => m.eventId))
    // 重整後那一輪不再是一片空白：事件流裡有這一筆，標記跟著回來（#1473）
    setNoOutputIds(view.messages.filter((m) => m.noOutput).map((m) => m.eventId))
    lastSequenceRef.current = view.lastSequence
    // 選單還原成這條對話最新一則實際用的模型；非 dev 伺服器不給，維持 null
    setConversationModel(view.currentModel ?? null)
  }

  // 啟動：帶 id 就載入既有訊息，沒帶就跟伺服器要一條新的。
  // conversationId 一律由伺服器配發，因為每一筆事件都要掛在真的存在的對話上。
  useEffect(() => {
    void (async () => {
      try {
        if (resumeId) {
          conversationIdRef.current = resumeId
          await reloadConversation()
          onConversationReady(resumeId)
          setReady(true)
          return
        }
        // 🔴 新對話**不在這裡建**：開個頁面就生一筆空對話，側欄會被沒有
        // 任何訊息的紀錄塞滿。改成第一次送出時才建（見 ensureConversation）。
        setReady(true)
      } catch (e) {
        setSetupError(resumeId ? '這條對話載入失敗' : (e as Error).message)
      }
    })()
    // 空 deps 是刻意的：這條對話的 id 一旦定下就不再變，重跑等於再開一條
  }, [])

  // 捲動跟隨（#1334）：自動捲到底與「展開工具紀錄」共用同一份狀態，
  // 時序全部收在 useScrollFollow 裡，那裡才測得到（#1404）。
  const {
    threadRef, handleThreadScroll, handleToolToggle, anchored,
  } = useScrollFollow([messages, status])

  // 留意見（#1446）：入口常駐、掛在每一輪最後一則助理訊息下方，送出後就地回顯。
  // 非偵錯模式不畫回饋按鈕，也就不必讀自己留過的意見（#1724）
  const { entries: myFeedback, submit: submitFeedback } = useConversationFeedback(
    debugMode ? feedbackConversationId : null,
  )
  const anchors = feedbackAnchors(messages)

  const busy = status === 'submitted' || status === 'streaming'

  // 工具跑起來之後可能好幾秒沒有任何 delta，秒數得自己走，否則會卡在原地。
  const [, setTick] = useState(0)
  useEffect(() => {
    if (!busy) return undefined
    const timer = window.setInterval(() => setTick((n) => n + 1), 200)
    return () => window.clearInterval(timer)
  }, [busy])

  // 一輪落地才通知側欄重拉：標題是伺服器在寫第一則使用者訊息時回填的，
  // 送出的當下去拉還拉不到它。
  useEffect(() => {
    if (messages.length > 0 && !busy) onTurnFinished()
  }, [busy, messages.length, onTurnFinished])

  // 🔴 一輪結束就重讀基準。那一輪伺服器寫了幾筆事件前端算不出來，
  // 不重讀的話下一次送出會拿著落後的 baseSequence，每一句都被判成衝突。
  useEffect(() => {
    const id = conversationIdRef.current
    if (busy || !id) return
    // 按停止的話先等伺服器收尾（turn_aborted 寫完）再讀，否則基準落後一筆、下一句 409
    void stopRequestRef.current
      .then(() => fetchConversationUserView(id))
      .then((view) => {
        lastSequenceRef.current = view.lastSequence
      })
      .catch(() => undefined)
  }, [busy])

  // 建議提問點了就直接送出那句話，不是填進輸入框讓使用者再按一次。
  const sendText = (raw: string): boolean => {
    const text = raw.trim()
    if (!text || busy || !ready) return false
    setStaleNotice(null)
    setSkipError(null)
    lastSentTextRef.current = text
    // 上一輪的「沒有回覆」標記只屬於上一輪，送出新的一句就清掉
    setLiveNoOutput(false)
    // 先確保對話存在再送：transport 的 prepareSendMessagesRequest 讀的是
    // conversationIdRef，沒有 id 這一則就掛不上任何一條對話。
    void ensureConversation()
      .then(() => sendMessage({ text }))
      .catch((e: Error) => setSetupError(e.message))
    return true
  }

  // 第一次送出才真的建一條。record-first 的順序不變：id 先拿到，事件才寫得下去。
  // 🔴 in-flight 用 ref 記住：連按兩次送出時 busy 還沒變 true，
  // 沒有這道保護就會同時建出兩條對話。
  const creatingRef = useRef<Promise<string> | null>(null)

  const ensureConversation = (): Promise<string> => {
    if (conversationIdRef.current) return Promise.resolve(conversationIdRef.current)
    if (creatingRef.current) return creatingRef.current
    creatingRef.current = createConversation().finally(() => {
      creatingRef.current = null
    })
    return creatingRef.current
  }

  const createConversation = async (): Promise<string> => {
    const res = await fetch('/api/ai/conversations', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // 身分不再由前端送：伺服器一律用 cvt 解出的 accountId／membershipId／villageId
      body: JSON.stringify({ title: null }),
    })
    if (!res.ok) throw new Error(`建立對話失敗（${res.status}）`)
    const created = (await res.json()).conversationId as string
    conversationIdRef.current = created
    setFeedbackConversationId(created)
    onConversationReady(created)
    return created
  }

  // 等待回答的那一份提問；串流中不算，那時參數還在產生（#1640）
  const pendingQuestion = busy ? null : findPendingQuestionPart(messages)
  const pendingQuestionId = pendingQuestion?.toolCallId ?? null
  const pendingQuestionCount = pendingQuestion ?
    readAskInput(pendingQuestion.input).questions.length : 0

  // 等你確認的那一張（#1693）；串流中不算
  const pendingConfirm = busy ? null : findPendingConfirmPart(messages)
  const pendingConfirmId = pendingConfirm?.toolCallId ?? null

  // 一輪結束（含確認被擋下）就解除處理中：結果已在卡片上，或重讀後回到伺服器的狀態
  useEffect(() => {
    if (!busy) setConfirmingId(null)
  }, [busy])

  // 面板消失（送出或關閉）時焦點跟著被卸載的按鈕掉到 body：還給輸入框（#1653）。
  // 直接回覆時焦點本來就在輸入框，不會命中
  const composerRef = useRef<HTMLTextAreaElement>(null)
  const hadPanelRef = useRef(false)
  useEffect(() => {
    const lost = !document.activeElement || document.activeElement === document.body
    if (hadPanelRef.current && !pendingQuestionId && lost) composerRef.current?.focus()
    hadPanelRef.current = pendingQuestionId !== null
  }, [pendingQuestionId])

  // 送出答案（#1642）：填進那一份提問，sendAutomaticallyWhen 接著送出續跑請求。
  // 🔴 只認目前等待回答的那一份；已有答案在路上時不再收，防連點送兩次
  const submitAnswers = (part: AnyToolPart, answers: SubmittedAnswer[]) => {
    if (busy || pendingAnswerRef.current || part.toolCallId !== pendingQuestionId) return
    setStaleNotice(null)
    setSkipError(null)
    setLiveNoOutput(false)
    pendingAnswerRef.current = { toolCallId: part.toolCallId, answers }
    void addToolOutput({
      tool: part.type === 'dynamic-tool' ? part.toolName : part.type.slice(5),
      toolCallId: part.toolCallId,
      output: toAnsweredOutput(readAskInput(part.input), answers),
    } as Parameters<typeof addToolOutput>[0])
  }

  // 略過等待回答的那一組（#1640）：成功就地標成未回答，失敗留下錯誤並回傳 false。
  // notices 給 ✕ 關閉用；沒給就用 api 的說法（講的是「這句話還沒送出」）
  const skipQuestionSet = async (
    conversationId: string, toolCallId: string, notices?: typeof DISMISS_NOTICES,
  ): Promise<boolean> => {
    setSkipping(true)
    setSkipError(null)
    try {
      const skipped = await skipPendingQuestion(conversationId, toolCallId)
      setMessages((current) => markQuestionUnanswered(
        current, toolCallId, { status: skipped.status, message: skipped.message },
      ))
      return true
    } catch (e) {
      const answered = e instanceof SkipQuestionError && e.code === ALREADY_ANSWERED_CODE
      const notice = answered ? notices?.alreadyAnswered : notices?.failed
      setSkipError(notice ?? (e as Error).message)
      // 已經回答過：重讀才畫得出當時的答案
      if (answered) void reloadConversation()
      return false
    } finally {
      setSkipping(false)
    }
  }

  // 確認寫入（#1693）：不帶訊息送出續跑請求，接著同一則助理訊息；結果由串流補回卡片
  const confirmToolCall = (part: AnyToolPart) => {
    if (busy || skipping || confirmingId || part.toolCallId !== pendingConfirmId) return
    setStaleNotice(null)
    setSkipError(null)
    setLiveNoOutput(false)
    setConfirmingId(part.toolCallId)
    pendingConfirmRef.current = part.toolCallId
    void sendMessage()
  }

  // 放棄等你確認的那一張（#1693）：按取消或直接打字。成功就地標成未執行，失敗留下錯誤
  const abandonConfirm = async (
    part: AnyToolPart, reason: 'cancel' | 'interrupt',
  ): Promise<boolean> => {
    const conversationId = conversationIdRef.current
    if (!conversationId) return false
    setSkipping(true)
    setSkipError(null)
    try {
      await skipPendingQuestion(conversationId, part.toolCallId, reason)
      const result = abandonedResult(toolNameOf(part), reason === 'cancel' ? 'cancelled' : 'interrupted')
      setMessages((current) =>
        markQuestionUnanswered(current, part.toolCallId, abandonedOutput(part, result)))
      return true
    } catch (e) {
      setSkipError(e instanceof SkipQuestionError ? CONFIRM_ABANDON_FAILED : (e as Error).message)
      void reloadConversation()
      return false
    } finally {
      setSkipping(false)
    }
  }

  // 有待回答的提問時，先略過、成功才送出那句話（#1640）。🔴 失敗就不送、字留在輸入框：
  // 這條對話在略過之前開不了新的一輪，硬送只會換來一個 409。
  const send = async () => {
    const text = input
    if (!text.trim() || busy || !ready || skipping) return
    const conversationId = conversationIdRef.current
    if (pendingQuestionId && conversationId &&
      !(await skipQuestionSet(conversationId, pendingQuestionId))) return
    // 確認卡比照：直接打字＝中斷，先記下再送（#1693）
    if (pendingConfirm && !(await abandonConfirm(pendingConfirm, 'interrupt'))) return
    if (sendText(text)) setInput('')
  }

  // 按 ✕ 整組不回答（#1653）：呼叫略過 API，面板隨提問變成未回答而消失
  const dismissQuestion = () => {
    const conversationId = conversationIdRef.current
    if (!pendingQuestionId || !conversationId || busy || skipping) return
    void skipQuestionSet(conversationId, pendingQuestionId, DISMISS_NOTICES)
  }

  // 一個字都還沒吐時最後一則仍是使用者訊息，標記就落在它後面——
  // 兩種情況都看得到「已停止」，不必分岔。
  const stopStreaming = () => {
    const last = messages[messages.length - 1]
    if (last) setAbortedIds((ids) => (ids.includes(last.id) ? ids : [...ids, last.id]))
    // 🔴 先告訴伺服器再斷線：瀏覽器斷線不會傳進容器，少了這一支模型會一路
    // 跑完、token 照算，重整後「停掉」的答案還會完整回來（#1413）。
    stopRequestRef.current = requestServerStop(conversationIdRef.current)
    void stop()
  }

  // 續談的那一條一開始就算「已開始」：等訊息載回來才切，會先閃一下置中的首屏。
  const started = messages.length > 0 || resumeId !== null

  return (
    <div className={`chat ${started ? 'started' : ''}`}>
      <main
        className={`thread ${anchored ? 'anchored' : ''}`}
        ref={threadRef}
        onScroll={handleThreadScroll}
      >
        {!started && (
          <div className="hero">
            <h1 className="hero-title">
              今天想從哪裡開始？
            </h1>
            <p className="hero-sub">
              {ready ? '活動、報名、名單整理，交給我處理。' : '正在建立對話…'}
            </p>
          </div>
        )}
        {/* 載入中要有東西看，不是一片空白 */}
        {resumeId && !ready && !setupError && (
          <p className="resuming">正在載入這條對話…</p>
        )}
        {setupError && (
          <div className="row assistant">
            <div className="bubble error">{setupError}</div>
          </div>
        )}
        {staleNotice && (
          <div className="row assistant">
            <div className="bubble error">{staleNotice}</div>
          </div>
        )}
        {messages.map((message, messageIndex) => {
          const hasParts = message.parts.some((part) => isToolUIPart(part) || isTextUIPart(part))
          const stopped = abortedIds.includes(message.id)
          const blank = message.role === 'assistant' && !hasParts && !busy
          // 重整後靠事件代號認；即時那一輪還沒有代號，掛在最後一則空訊息上
          const noOutput = noOutputIds.includes(message.id) ||
            (liveNoOutput && blank && messageIndex === messages.length - 1)
          // 🔴 照 parts 的原始順序畫，只把「連續」的工具併成一行摘要。
          // 全部收成一行會讓「想一段→查→再想一段」的推導順序在結構上消失。
          // 步界（step-start）交給 isStepBoundary 略過：歷史沒有它，切了兩邊不一致（#1417）。
          return (
            <Fragment key={message.id}>
              {groupConsecutiveTools(message.parts, isGroupableTool, isStepBoundary).map((item) => {
                // 附件卡片掛在那一組工具下方（#1620），資料就是工具結果本身
                if (item.kind === 'tools') {
                  const tools = item.tools as AnyToolPart[]
                  return (
                    <Fragment key={tools[0].toolCallId}>
                      <ToolGroup
                        parts={tools} onToggle={handleToolToggle} showDetails={debugMode}
                      />
                      <ToolAttachments parts={tools} onOpen={setPreviewDocumentId} />
                    </Fragment>
                  )
                }
                const { part, index } = item
                // 提問在對話區只留開場白與結果，作答在輸入框上方的面板（#1653）
                if (isQuestionSetPart(part) && isToolUIPart(part)) {
                  return <QuestionRecord key={part.toolCallId} part={part} />
                }
                // 確認卡在對話區就地作答（#1693）：按鈕與狀態都在卡片上
                if (isConfirmCardPart(part)) {
                  return (
                    <ConfirmCard
                      key={part.toolCallId}
                      part={part}
                      status={confirmCardStatus(part, part.toolCallId === confirmingId)}
                      disabled={busy || skipping || part.toolCallId !== pendingConfirmId}
                      onConfirm={() => confirmToolCall(part)}
                      onCancel={() => void abandonConfirm(part, 'cancel')}
                    />
                  )
                }
                if (isToolUIPart(part)) {
                  return (
                    <Fragment key={part.toolCallId}>
                      <ToolCard
                        part={part} onToggle={handleToolToggle} showDetails={debugMode}
                      />
                      <ToolAttachments parts={[part]} onOpen={setPreviewDocumentId} />
                    </Fragment>
                  )
                }
                // 思考僅偵錯模式渲染（#1739）
                if (part.type === 'reasoning') {
                  return debugMode ? <ReasoningBlock key={`r${index}`} text={part.text} /> : null
                }
                // 空白字元不畫氣泡（#1437）：模型在兩次查詢之間常只吐一個換行
                if (!isTextUIPart(part) || !hasVisibleText(part.text)) return null
                return (
                  <div key={`t${index}`} className={`row ${message.role}`}>
                    <div className="bubble">
                      {message.role === 'assistant' ? (
                        <div className="markdown">
                          <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>{part.text}</ReactMarkdown>
                        </div>
                      ) : part.text}
                    </div>
                  </div>
                )
              })}
              {stopped && (
                <div className="row assistant"><span className="stopped">已停止</span></div>
              )}
              {noOutput && !stopped && (
                <div className="row assistant">
                  <span className="no-output">這一次模型沒有回覆，可以再問一次</span>
                </div>
              )}
              {debugMode && anchors.has(messageIndex) && (
                <FeedbackMenu
                  turn={anchors.get(messageIndex) as number}
                  label="對這個回答留下意見"
                  entries={feedbackForTurn(myFeedback, anchors.get(messageIndex) as number)}
                  onSubmit={submitFeedback}
                />
              )}
            </Fragment>
          )
        })}
        {/* 模型還在跑就一直墊在最新內容下方，吐完或停止才消失；取代原本的「正在思考」 */}
        {busy && (
          <div className="row assistant running" role="status">
            <img className="running-logo" src="/logo-running.png" alt="正在回覆" />
          </div>
        )}
        {/* 錯誤顯示在對話流裡，不是 console。useChat 的 error 是整個 chat 一份。 */}
        {error && error.message === PENDING_QUESTION_NOTICE && (
          <div className="row assistant">
            <div className="bubble notice">{PENDING_QUESTION_NOTICE}</div>
          </div>
        )}
        {error && error.message !== PENDING_QUESTION_NOTICE && (
          <div className="row assistant">
            <div className="bubble error">回應失敗：{error.message}</div>
          </div>
        )}
        {skipError && (
          <div className="row assistant">
            <div className="bubble error">{skipError}</div>
          </div>
        )}
      </main>

      {/* 整份對話的意見送輪次 0。🔴 不做關窗意見調查攔截：那一刻跳出調查最惹人厭。
          放在 composer 外面，才不會被 .composer 的圓鈕與輸入框樣式吃掉。 */}
      {debugMode && started && (
        <div className="feedback-whole">
          <FeedbackMenu
            turn={WHOLE_CONVERSATION_TURN}
            label="對整份對話留下意見"
            entries={feedbackForTurn(myFeedback, WHOLE_CONVERSATION_TURN)}
            onSubmit={submitFeedback}
          />
        </div>
      )}

      {/* 只有最後一輪、仍在等待回答的那一組出現面板（#1653）。
          放在 composer 外面，才不會被 .composer 的圓鈕樣式吃掉。 */}
      {/* 常駐的播報區：面板出現時告訴螢幕閱讀器有新提問，不搶焦點 */}
      <p className="sr-only" role="status" aria-live="polite">
        {pendingQuestionCount > 0 ?
          `有 ${pendingQuestionCount} 題要問你，提問面板在輸入框上方。` : ''}
      </p>
      {pendingQuestion && (
        <div className="ask-panel-wrap">
          <QuestionPanel
            key={pendingQuestion.toolCallId}
            input={readAskInput(pendingQuestion.input)}
            initialDrafts={draftsRef.current.get(pendingQuestion.toolCallId)}
            onDraftsChange={(drafts) => draftsRef.current.set(pendingQuestion.toolCallId, drafts)}
            onSubmit={(answers) => submitAnswers(pendingQuestion, answers)}
            onDismiss={dismissQuestion}
            dismissing={skipping}
          />
        </div>
      )}

      <footer className="composer">
        {/* dev 才有模型切換（#1726）；實驗參數（#1727）只在偵錯模式。選擇沿用到下一則 */}
        {((modelOptions.length > 0 && selectedModel) || (experiment && stepBounds)) && (
          <div className="model-picker">
            {modelOptions.length > 0 && selectedModel && (
              <label>
                模型
                <select
                  value={toChoiceValue(selectedModel, debugMode)}
                  disabled={busy}
                  onChange={(e) => {
                    const choice = parseChoiceValue(e.target.value)
                    chooseModel(choice.model, choice.debug)
                  }}
                >
                  {/* 每個模型多一項「-debug」假選項：送出的 model 相同，只打開偵錯元素 */}
                  {modelOptions.flatMap((option) => [false, true].map((asDebug) => {
                    const value = toChoiceValue(option.id, asDebug)
                    return (
                      <option key={value} value={value}>
                        {asDebug ? `${option.displayName}-debug` : option.displayName}
                      </option>
                    )
                  }))}
                </select>
              </label>
            )}
            {experiment && stepBounds && (
              <>
                <label>
                  步數上限
                  <input
                    type="number"
                    className="experiment-steps"
                    min={stepBounds.min}
                    max={stepBounds.max}
                    value={experiment.maxSteps}
                    disabled={busy}
                    onChange={(e) => {
                      const steps = clampSteps(e.target.value, stepBounds)
                      if (steps !== null) updateExperiment({ maxSteps: steps })
                    }}
                  />
                </label>
                <label>
                  <input
                    type="checkbox"
                    className="experiment-compress"
                    checked={experiment.compressToolResults}
                    disabled={busy}
                    onChange={(e) => updateExperiment({ compressToolResults: e.target.checked })}
                  />
                  同輪工具結果壓縮
                </label>
                {thinkingBounds && experiment.thinkingBudget !== undefined && (
                  <label>
                    思考預算
                    <select
                      className="experiment-thinking"
                      value={experiment.thinkingBudget}
                      disabled={busy}
                      onChange={(e) => updateExperiment({ thinkingBudget: Number(e.target.value) })}
                    >
                      {thinkingBudgetOptions(thinkingBounds).map((value) => (
                        <option key={value} value={value}>{value}</option>
                      ))}
                    </select>
                  </label>
                )}
              </>
            )}
          </div>
        )}
        <div className="composer-box">
          {/* 操作提示放在下方註記而非 placeholder：375px 時長 placeholder 會折行被切掉 */}
          <textarea
            ref={composerRef}
            value={input}
            placeholder={pendingQuestionId || pendingConfirmId ? SKIP_PLACEHOLDER : '輸入訊息…'}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' || e.shiftKey) return
              // 🔴 注音等輸入法組字中，Enter 是「確認候選字」不是送出。
              // 少了這道判斷，中文使用者選字時會把半成品送出去。
              // keyCode 229 是舊版瀏覽器沒有 isComposing 時的等價訊號。
              if (e.nativeEvent.isComposing || e.nativeEvent.keyCode === 229) return
              e.preventDefault()
              void send()
            }}
          />
          {busy ? (
          // 串流中送出鈕就地變停止鈕：按了立刻停，不等這一輪跑完。
            <button className="stop" aria-label="停止" title="停止" onClick={stopStreaming}>
              <StopIcon />
            </button>
          ) : (
            <button
              aria-label="送出" title="送出"
              onClick={() => void send()} disabled={!ready || !input.trim() || skipping}
            >
              <Icon d={PATH.arrowUp} size={18} />
            </button>
          )}
        </div>
        <p className="composer-note">
          Enter 送出、Shift+Enter 換行・gemini-3.5-flash-lite・回答可能有誤，重要決定請自行查證
        </p>
      </footer>

      {/* key 換公文就重掛，前一份的內容不會閃在新的那一份上 */}
      {previewDocumentId && (
        <DocumentPreview
          key={previewDocumentId} documentId={previewDocumentId} onClose={closePreview}
        />
      )}

      {/* 空狀態才出現。它是 composer 的兄弟節點而非包住它，
          所以兩態切換時輸入框不會被重掛，已打的字與焦點都留著。 */}
      {!started && (
        <SuggestionBar
          suggestions={quickQas}
          disabled={!ready}
          // 只帶進輸入框，不自動送出：使用者常要先改一改再問
          onPick={setInput}
        />
      )}
    </div>
  )
}
